import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { FaArrowLeft, FaStar, FaArrowRight, FaRegStar, FaMoon, FaSun } from 'react-icons/fa';
import '../styles/DetailedLocationView.css';
import { GaugeSection } from '../components/GaugeSection';
import { useAuth } from '../context/AuthContext';
import { useDataCache } from '../context/DataCacheContext';
import { checkIfFavorite, addToFavorites, removeFromFavorites } from '../services/favoritesService';
import * as locationService from '../services/locationService';

// Define backend API base URL 
const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5001/api';

// Helper to check if a string is a valid Firestore document ID (20+ chars, alphanumeric)
const isFirestoreId = (id) => /^[A-Za-z0-9]{20,}$/.test(id);

const DetailedLocationView = () => {
    const { cityName, locationId } = useParams();
    const navigate = useNavigate();
    const { currentUser, idToken: token } = useAuth();
    const { 
        cachedReviews, 
        loadReviews,
    } = useDataCache();
    
    const [location, setLocation] = useState(null);
    const [loading, setLoading] = useState(true);
    // const [reviews, setReviews] = useState([]); // This will be replaced by cachedReviews
    const [rating, setRating] = useState(0);
    const [comment, setComment] = useState('');
    const [averageRating, setAverageRating] = useState(0);
    const [showReviews, setShowReviews] = useState(false);
    const [showReviewForm, setShowReviewForm] = useState(false);
    const [isDescriptionExpanded, setIsDescriptionExpanded] = useState(true);

    const [additionalInfo, setAdditionalInfo] = useState({
        text: '',
        links: []
    });
    const [isFavorite, setIsFavorite] = useState(false);
    const [isDarkMode, setIsDarkMode] = useState(() => localStorage.getItem('theme') === 'dark');
    const [showConfirmation, setShowConfirmation] = useState(false);
    const MAX_COMMENT_LENGTH = 200;
    const [activeSection, setActiveSection] = useState('keyInfo');
    const [submitError, setSubmitError] = useState(null);

    const [selectedImage, setSelectedImage] = useState(null);
    const [isModalOpen, setIsModalOpen] = useState(false);

    const cacheKey = `${cityName}_${locationId}`;
    const reviews = useMemo(() => cachedReviews[cacheKey] || [], [cachedReviews, cacheKey]);

    // Moved up to fix no-use-before-define
    const calculateAverageRating = useCallback((reviewsData) => {
        if (!reviewsData || reviewsData.length === 0) { setAverageRating(0); return; }
        const total = reviewsData.reduce((acc, review) => acc + review.rating, 0);
        setAverageRating(total / reviewsData.length);
    }, []);

    // Add carousel handler (move this above its first usage)
    const handleCarousel = (direction) => {
      if (!images.length) return;
      const currentIdx = images.indexOf(selectedImage);
      let newIdx = currentIdx + direction;
      if (newIdx < 0) newIdx = images.length - 1;
      if (newIdx >= images.length) newIdx = 0;
      setSelectedImage(images[newIdx]);
    };

    // Fetch location details from backend
    useEffect(() => {
        let isMounted = true;
        const fetchLocation = async () => {
            setLoading(true);
            try {
                let docId = locationId;
                if (!isFirestoreId(locationId)) {
                    // Try to resolve slug to document ID
                    const response = await fetch(`${API_BASE_URL}/locations/resolve-slug/${encodeURIComponent(locationId)}`);
                    if (response.ok) {
                        const data = await response.json();
                        docId = data.documentId;
                    } else {
                        setLocation(null);
                        setLoading(false);
                        return;
                    }
                }
                const data = await locationService.getPointOfInterest(cityName, docId, token);
                if (isMounted) {
                    if (data && data.id) {
                        setLocation(data);
                        // Set the initial selected image for the gallery from the fetched data
                        if (data.images && data.images.length > 0) {
                            setSelectedImage(data.images[0]);
                        }
                        setAdditionalInfo({
                            text: data.additionalInfo?.text || data.moreInfo || '',
                            links: data.additionalInfo?.links || []
                        });
                    } else {
                        setLocation(null);
                    }
                }
            } catch (error) {
                console.error("Failed to fetch location:", error);
                setLocation(null);
            } finally {
                if (isMounted) setLoading(false);
            }
        };
        fetchLocation();
        return () => { isMounted = false; };
    }, [cityName, locationId, token]);

    // Fetch reviews using the centralized DataCacheContext
    useEffect(() => {
        if (cityName && locationId) {
            loadReviews(cityName, locationId, token);
        }
    }, [cityName, locationId, token, loadReviews]);


    // Fetch ratings from backend (polling every 60s)
  

    useEffect(() => {
        const checkFavoriteStatus = async () => {
            if (currentUser && locationId && cityName && token) {
                const favoriteId = await checkIfFavorite(token, cityName, locationId);
                setIsFavorite(!!favoriteId);
            }
        };
        
        checkFavoriteStatus();
    }, [currentUser, locationId, cityName, token]);

    useEffect(() => {
        calculateAverageRating(reviews);
    }, [reviews, calculateAverageRating]);
    
    useEffect(() => {
        // Save theme preference to localStorage
        localStorage.setItem('theme', isDarkMode ? 'dark' : 'light');
    }, [isDarkMode]);

    const handleSubmit = async (e) => {
        e.preventDefault();
        setSubmitError(null);
        if (rating < 1 || rating > 5) { setSubmitError("Rating must be between 1 and 5."); return; }
        if (!currentUser || !token) { setSubmitError("You must be logged in to leave a review."); return; }
        
        try {
            const response = await fetch(`${API_BASE_URL}/reviews/${encodeURIComponent(cityName)}/${encodeURIComponent(locationId)}/reviews`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
                body: JSON.stringify({ rating, comment, locationName: location?.name })
            });
            
            if (!response.ok) { 
                const errData = await response.json(); 
                throw new Error(errData.error || `HTTP error ${response.status}`); 
            }
            
            setComment(''); 
            setRating(0);
            setShowConfirmation(true); 
            setShowReviewForm(false);
            setTimeout(() => setShowConfirmation(false), 3000);
            
        } catch (error) {
            console.error('Error submitting review:', error);
            setSubmitError(error.message || 'Failed to submit review.');
        }
    };

    const handleCommentChange = (e) => {
        const value = e.target.value;
        
        // Count line breaks to enforce line limit (max 8 lines)
        const lineCount = (value.match(/\n/g) || []).length + 1;
        
        // Only update if we're within character limit and line limit
        if (value.length <= MAX_COMMENT_LENGTH && lineCount <= 8) {
            // Limit consecutive newlines to prevent layout issues
            const limitedNewlines = value.replace(/\n{3,}/g, '\n\n');
            setComment(limitedNewlines);
        } else if (lineCount > 8 && comment.length < value.length) {
            // If line count exceeds limit but character count is increasing, prevent the change
            return;
        } else if (value.length <= MAX_COMMENT_LENGTH) {
            // If character count is decreasing, allow the change even if lines > 8
            const limitedNewlines = value.replace(/\n{3,}/g, '\n\n');
            setComment(limitedNewlines);
        }
    };

    const handleBack = () => {
        navigate(`/city/${cityName}`);
    };

    const openImageModal = () => {
        setIsModalOpen(true);
    };

    const closeImageModal = () => {
        setIsModalOpen(false);
    };

    const handleThumbnailClick = (image) => {
        setSelectedImage(image);
    };

    // Remove only the ratings state, handleRatingSubmit, and any other rating/gauge user interaction code
    // Do not touch favorites or any other unrelated code

    const handleFavoriteToggle = async () => {
        if (!currentUser || !token) { 
            alert("You must be logged in to manage favorites."); 
            return; 
        }
        if (!location) { 
            console.error("Cannot toggle favorite: location data not loaded."); 
            alert("Location data hasn't loaded yet, please try again shortly.");
            return; 
        }

        try {
            let result;
            if (isFavorite) {
                result = await removeFromFavorites(token, cityName, locationId);
            } else {
                result = await addToFavorites(token, cityName, locationId, location.name); 
            }
            
            if (result.success) {
                setIsFavorite(!isFavorite);
            } else { 
                console.error("Failed to update favorites:", result.message);
                alert(`Failed to update favorites: ${result.message || 'Please try again.'}`); 
            }
        } catch (error) { 
            console.error("Error toggling favorite:", error); 
            alert("An error occurred while updating favorites."); 
        }
    };

    // Toggle theme function
    const toggleTheme = () => {
        setIsDarkMode(!isDarkMode);
    };

    const scrollToSection = (sectionId) => {
        const section = document.getElementById(sectionId);
        if (section) {
            // Get the height of the sticky navigation
            const navHeight = document.querySelector('.contents-navigation').offsetHeight;
            
            // Calculate the scroll position
            const offsetPosition = section.offsetTop - navHeight - 25; // Increased padding for smoother appearance
            
            // Scroll with a smoother behavior - longer duration
            window.scrollTo({
                top: offsetPosition,
                behavior: 'smooth'
            });
            
            // Use setTimeout to give a slight delay before updating active section
            // This makes the transition feel more natural
            setTimeout(() => {
                setActiveSection(sectionId);
            }, 100);
        }
    };

    if (loading) return <div className="loading">Loading...</div>;
    if (!location) return <div className="error">Location not found</div>;

    // Correctly get the images array from the location state
    const images = location?.images || [];

    return (
        <div className={`detailed-view-page ${isDarkMode ? 'dark-mode' : 'light-mode'}`}>
            {/* Navigation Header */}
            <div className="location-header">
                <div className="header-left">
                    <button className="back-button" onClick={handleBack}>
                        <FaArrowLeft />
                    </button>
                    {/* 1. Update location title for truncation and tooltip */}
                    <h1 className="location-title" title={location.name}>
                        <span className="location-title-text">{location.name}</span>
                    </h1>
                </div>
                <div className="header-right">
                    <button 
                        className="theme-toggle-button" 
                        onClick={toggleTheme}
                        aria-label={isDarkMode ? "Switch to light mode" : "Switch to dark mode"}
                    >
                        {isDarkMode ? <FaSun /> : <FaMoon />}
                    </button>
                    <button 
                        className={`favorite-button ${isFavorite ? 'is-favorite' : ''}`}
                        onClick={handleFavoriteToggle}
                        title={isFavorite ? 'Remove from Favorites' : 'Add to Favorites'}
                    >
                        {isFavorite ? <FaStar /> : <FaRegStar />} 
                        <span className="favorite-button-text">
                            {isFavorite ? 'Remove from Favorites' : 'Add to Favorites'}
                        </span>
                    </button>
                </div>
            </div>
            
            {/* Contents Navigation - Moved up to be directly below header */}
            <div className="contents-navigation">
                <div className="contents-wrapper">
                    <ul>
                        <li className={activeSection === 'keyInfo' ? 'active' : ''}>
                            <a href="#keyInfo" onClick={(e) => { e.preventDefault(); scrollToSection('keyInfo'); }}>Key Info</a>
                        </li>
                        <li className={activeSection === 'description' ? 'active' : ''}>
                            <a href="#description" onClick={(e) => { e.preventDefault(); scrollToSection('description'); }}>Description</a>
                        </li>
                        <li className={activeSection === 'gallery' ? 'active' : ''}>
                            <a href="#gallery" onClick={(e) => { e.preventDefault(); scrollToSection('gallery'); }}>Gallery</a>
                        </li>
                        <li className={activeSection === 'reviews' ? 'active' : ''}>
                            <a href="#reviews" onClick={(e) => { e.preventDefault(); scrollToSection('reviews'); }}>Reviews</a>
                        </li>
                        <li className={activeSection === 'community' ? 'active' : ''}>
                            <a href="#community" onClick={(e) => { e.preventDefault(); scrollToSection('community'); }}>More Info</a>
                        </li>
                    </ul>
                </div>
            </div>

            {/* Hero Section - As background for content */}
            <section className="hero-section">
                {/* Use the first image of the dynamic array for the hero background */}
                <img src={images.length > 0 ? images[0] : ''} alt={location.name} className="hero-image" />
                <div className="hero-content">
                    <p className="hero-description">{location.shortDescription}</p>
                </div>
            
                {/* Key Information Section - Centered on the hero image */}    
                <div className="key-info-container">
                    <section id="keyInfo" className="key-info-section">
                        <div className="section-header">
                            <h2>Key Information</h2>
                        </div>
                        
                        <div className="modular-grid">
                            {/* Address Tile */}
                            <div className="info-tile">
                                <h3>Address</h3>
                                <p className="tile-content">{location.address}</p>
                                <span 
                                    className="directions-link"
                                    onClick={() => window.open(`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(location.address)}`, '_blank')}
                                >
                                    Get directions <FaArrowRight />
                                </span>
                            </div>
                            
                            {/* Hours Tile */}
                            <div className="info-tile">
                                <h3>Opening Hours</h3>
                                <p className="tile-content">{location.openingHours}</p>
                            </div>
                            
                            {/* Affordability Rating Tile */}
                            <div className="rating-tile">
                                <h3>Affordability</h3>
                                <GaugeSection 
                                    rating={{ average: location.costRating || 50, count: 0 }}
                                    onRatingSubmit={() => {}}
                                    compact={true}
                                    icon="€"
                                    static={true}
                                />
                            </div>
                            
                            {/* Fun Rating Tile */}
                            <div className="rating-tile">
                                <h3>Fun Factor</h3>
                                <GaugeSection 
                                    rating={{ average: location.funRating || 50, count: 0 }}
                                    onRatingSubmit={() => {}}
                                    compact={true}
                                    icon="★"
                                    static={true}
                                />
                            </div>
                            
                            {/* Safety Rating Tile */}
                            <div className="rating-tile">
                                <h3>Safety</h3>
                                <GaugeSection 
                                    rating={{ average: location.safetyRating || 50, count: 0 }}
                                    onRatingSubmit={() => {}}
                                    compact={true}
                                    icon="✓"
                                    static={true}
                                />
                            </div>
                        </div>
                    </section>
                </div>
            </section>

            {/* Content below the hero section with no side margins */}
            <div className="content-wrapper">
                {/* Detailed Description */}
                <section id="description" className="detailed-description">
                    <div 
                        className="description-header"
                        onClick={() => setIsDescriptionExpanded(!isDescriptionExpanded)}
                    >
                        <h2>Detailed Description</h2>
                        <span className="toggle-icon">{isDescriptionExpanded ? '−' : '+'}</span>
                    </div>
                    {isDescriptionExpanded && (
                        <div className="description-content">
                            <p>{location.longDescription || 'No detailed description available.'}</p>
                        </div>
                    )}
                </section>

                {/* Photo Gallery */}
                <section id="gallery" className="photo-gallery">
                    <h2>Photo Gallery</h2>
                    {/* 2. Update gallery-container and thumbnails-container for carousel modal */}
                    <div className="gallery-container">
                        <div className="main-image-container">
                            <img src={selectedImage} alt="Selected" className="main-image" />
                            <button className="view-full-size-button" onClick={openImageModal}>
                                View Full Size
                            </button>
                        </div>
                        <div className="thumbnails-container">
                            {images.map((image, index) => (
                                <img
                                    key={index}
                                    src={image.replace('/upload/', '/upload/w_150,h_100,c_fill/')}
                                    alt={`Thumbnail ${index + 1}`}
                                    className={`thumbnail ${selectedImage === image ? 'active' : ''}`}
                                    onClick={() => handleThumbnailClick(image)}
                                />
                            ))}
                        </div>
                    </div>
                </section>

                {/* Student Reviews - Updated */}
                <section id="reviews" className="student-reviews-dark compact">
                    <div className="reviews-header" onClick={() => setShowReviews(!showReviews)}>
                        <h2>Student Reviews</h2>
                        <div className="reviews-summary">
                            <span>{averageRating.toFixed(1)} <FaStar color="#4CAF50" /></span>
                            <span>({reviews.length})</span>
                            <span className="toggle-icon">{showReviews ? '−' : '+'}</span>
                        </div>
                    </div>

                    {showReviews && (
                        <div className="reviews-content">
                            <button 
                                className="add-review-button"
                                onClick={() => setShowReviewForm(!showReviewForm)}
                                disabled={!currentUser || !currentUser.is_verified_student}
                                title={
                                    !currentUser
                                        ? "Please log in to add a review."
                                        : !currentUser.is_verified_student
                                        ? "Student verification is required to add a review."
                                        : showReviewForm ? 'Cancel Review' : 'Add Review'
                                }
                            >
                                {showReviewForm ? 'Cancel Review' : 'Add Review'}
                            </button>

                            {showReviewForm && currentUser && (
                                <form onSubmit={handleSubmit} className="review-form-dark">
                                    <div className="rating-slider">
                                        <div className="star-rating">
                                            {[...Array(5)].map((_, index) => (
                                                <FaStar 
                                                    key={index} 
                                                    color={index < rating ? "#4CAF50" : isDarkMode ? "#3a3f48" : "#e0e0e0"}
                                                    onClick={() => setRating(index + 1)}
                                                    style={{ cursor: 'pointer' }}
                                                />
                                            ))}
                                        </div>
                                    </div>
                                    <div className="comment-section-dark">
                                        <div className="textarea-container">
                                            <textarea
                                                value={comment}
                                                onChange={handleCommentChange}
                                                placeholder={
                                                    currentUser && !currentUser.is_verified_student
                                                        ? "Student verification is required to add a review."
                                                        : "Leave a comment"
                                                }
                                                maxLength={MAX_COMMENT_LENGTH}
                                                required
                                                disabled={!currentUser || !currentUser.is_verified_student}
                                            />
                                            <div className="character-count">
                                                <span className="line-limit">
                                                    {(comment.match(/\n/g) || []).length + 1}/8 lines
                                                </span>
                                                <span>
                                                    {MAX_COMMENT_LENGTH - comment.length} characters remaining
                                                </span>
                                            </div>
                                        </div>
                                        
                                        {showConfirmation && (
                                            <div className="confirmation-message">
                                                <FaStar className="confirmation-icon" />
                                                Review submitted successfully!
                                            </div>
                                        )}
                                        
                                        {submitError && <div className="review-submit-error">{submitError}</div>}
                                        
                                        <button
                                            type="submit"
                                            disabled={!currentUser || !currentUser.is_verified_student}
                                        >
                                            Submit
                                        </button>
                                    </div>
                                </form>
                            )}

                            <div className="reviews-list">
                                {reviews.length > 0 ? (
                                    reviews.map((review) => (
                                        <div key={review.id} className="review-card-dark">
                                            <div className="review-header">
                                                <span className="reviewer">
                                                    {review.displayUsername || 'Anonymous'}
                                                </span>
                                                <span className="review-date">
                                                    {review.timestamp ? new Date(review.timestamp).toLocaleDateString() : 'Unknown date'}
                                                </span>
                                                <div className="stars">
                                                    {[...Array(5)].map((_, i) => (
                                                        <FaStar 
                                                            key={i} 
                                                            color={i < review.rating ? "#4CAF50" : isDarkMode ? "#3a3f48" : "#e0e0e0"}
                                                            size={14}
                                                        />
                                                    ))}
                                                </div>
                                            </div>
                                            <p>{review.comment}</p>
                                        </div>
                                    ))
                                ) : (
                                    <div className="no-reviews">No reviews yet. Be the first to review!</div>
                                )}
                            </div>
                        </div>
                    )}
                </section>

                {/* Community & Social Features - Updated */}
                <section id="community" className="community-section">
                    <h2>Additional Information and Connected Pages</h2>
                    <div className="community-content">
                        {additionalInfo.text ? (
                            <p>{additionalInfo.text}</p>
                        ) : (
                            <p>No additional information available.</p>
                        )}
                        
                        {additionalInfo.links && additionalInfo.links.length > 0 && (
                            <div className="related-links">
                                <h3>Related Links</h3>
                                <ul className="links-list">
                                    {additionalInfo.links.map((link, index) => (
                                        <li key={index}>
                                            <a 
                                                href={link.url} 
                                                target="_blank" 
                                                rel="noopener noreferrer"
                                                className="external-link"
                                            >
                                                {link.text} <FaArrowRight />
                                            </a>
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        )}
                    </div>
                </section>
            </div>

            {/* Modal */}
            {/* 3. Modal with carousel */}
            {isModalOpen && (
                <div className="image-modal-overlay" onClick={closeImageModal}>
                    <div className="image-modal-carousel" onClick={e => e.stopPropagation()}>
                        <button className="carousel-arrow left" onClick={() => handleCarousel(-1)} aria-label="Previous image">&#8592;</button>
                        <img src={selectedImage} alt="Modal view" className="modal-content" />
                        <button className="carousel-arrow right" onClick={() => handleCarousel(1)} aria-label="Next image">&#8594;</button>
                        <span className="close-modal" onClick={closeImageModal}>&times;</span>
                        <div className="carousel-thumbnails">
                            {images.map((image, idx) => (
                                <img
                                    key={idx}
                                    src={image.replace('/upload/', '/upload/w_60,h_40,c_fill/')}
                                    alt={`Thumb ${idx + 1}`}
                                    className={`carousel-thumb ${selectedImage === image ? 'active' : ''}`}
                                    onClick={() => setSelectedImage(image)}
                                />
                            ))}
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default DetailedLocationView; 